import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";

import AppNextBtn from "./AppNextBtn2";
import AppPreviousBtn from "./AppPreviousBtn2";

import { ReactComponent as MenuIcon } from "../assets/images/menu2.svg";
import { ReactComponent as TickIcon } from "../assets/images/tick_green.svg";
import { ReactComponent as InfoIcon } from "../assets/images/info2.svg";

import { CATEGORY_LIST_HEIGHT } from "./RestaurantTemplate2";
import { CATEGORY_GRP_DEFAULT_POS } from "../constants";

let WINDOW_HEIGHT = window.innerHeight;

// ── Animation timing ─────────────────────────────────────────────────────────
const ENTER_DURATION = 700;   // ms – pop-in keyframe duration
const VISIBLE_DURATION = 5000;  // ms – button stays fully on-screen
const EXIT_DURATION = 500;   // ms – pop-out keyframe duration
const HIDDEN_DURATION = 1500;  // ms – gap before next cycle

// ── Star config ───────────────────────────────────────────────────────────────
const STAR_COUNT = 8;
const STAR_COLORS = ['#FFD700', '#FF69B4', '#00BFFF', '#7FFF00', '#FFD700', '#FF69B4', '#FF4500', '#00BFFF'];

// ── Inject keyframes once when module loads ───────────────────────────────────
if (typeof document !== 'undefined') {
  const styleId = 'add-to-cart-animations';
  if (!document.getElementById(styleId)) {
    const el = document.createElement('style');
    el.id = styleId;
    el.textContent = `
      @keyframes addToCartPopIn {
        0%   { transform: scale(0);   opacity: 0;    }
        65%  { transform: scale(1.2); opacity: 1;    }
        100% { transform: scale(1);   opacity: 0.75; }
      }
      @keyframes addToCartPopOut {
        0%   { transform: scale(1);   opacity: 0.75; }
        30%  { transform: scale(1.1); opacity: 0.9;  }
        100% { transform: scale(0);   opacity: 0;    }
      }
      @keyframes addToCartStarBurst {
        0%   { transform: rotate(var(--star-angle)) translateX(0px)  scale(1.2); opacity: 1; }
        100% { transform: rotate(var(--star-angle)) translateX(58px)  scale(0);   opacity: 0; }
      }
    `;
    document.head.appendChild(el);
  }
}

// ── Star particles – radial burst around the button ───────────────────────────
const StarParticles = () => (
  <Box sx={{ position: 'absolute', top: '50%', left: '50%', width: 0, height: 0, pointerEvents: 'none', zIndex: 10 }}>
    {Array.from({ length: STAR_COUNT }, (_, i) => {
      const angle = (i / STAR_COUNT) * 360;
      const size = i % 2 === 0 ? 8 : 5;
      const half = size / 2;
      return (
        <Box
          key={i}
          style={{ '--star-angle': `${angle}deg` }}
          sx={{
            position: 'absolute',
            width: `${size}px`,
            height: `${size}px`,
            top: `-${half}px`,
            left: `-${half}px`,
            borderRadius: i % 3 === 0 ? '50%' : '2px',
            backgroundColor: STAR_COLORS[i],
            boxShadow: `0 0 6px ${STAR_COLORS[i]}`,
            animation: `addToCartStarBurst 0.6s ease-out ${i * 0.055}s both`,
          }}
        />
      );
    })}
  </Box>
);

const BtnView = ({
  enterAframeAr,
  enterWebXrArMode,
  showPrevBtn,
  showNextBtn,
  prevIcon,
  nextIcon,
  locale,
  menuIcon,
  onPrevBtnClick,
  onNextBtnClick,
  onRestMenuNavigate,
}) => {
  let posStyle = {};
  if (enterAframeAr) {
    posStyle = { bottom: 15 };
  }
  if (enterWebXrArMode) {
    posStyle = { top: WINDOW_HEIGHT + 40 };
  }

  return (
    <>
      <Box sx={{ ...styles.prevMainView, ...posStyle }}>
        {showPrevBtn ? (
          <AppPreviousBtn
            locale={locale}
            image={prevIcon}
            isCircle={false}
            onPrevBtnClick={onPrevBtnClick}
          />
        ) : null}
      </Box>
      {enterAframeAr || enterWebXrArMode ? null : (
        <Box
          style={{
            ...styles.menuBtnView,
            ...(menuIcon ? { width: 90, height: 90 } : null),
          }}
        >
          {menuIcon ? (
            <img
              src={menuIcon}
              width={"100%"}
              height={"100%"}
              onClick={() => onRestMenuNavigate()}
            />
          ) : (
            <MenuIcon
              width={"100%"}
              height={"100%"}
              onClick={() => onRestMenuNavigate()}
            />
          )}
        </Box>
      )}
      <Box sx={{ ...styles.nextMainView, ...posStyle }}>
        {showNextBtn ? (
          <>
            <AppNextBtn
              locale={locale}
              image={nextIcon}
              isCircle={false}
              onNextBtnClick={onNextBtnClick}
            />
          </>
        ) : null}
      </Box>
    </>
  );
};

const AppNextPrevBtnTemp2 = (props) => {
  const {
    enterAframeAr,
    enterWebXrArMode,
    showPrevBtn,
    showNextBtn,
    prevIcon,
    nextIcon,
    menuSectionBg,
    categoryGrpPosition = CATEGORY_GRP_DEFAULT_POS,
    showCategoryGrp = false,
    handleAddToCart,
    onAdjustCartItem,
    onViewCart,
    cartItems = [],
    cartItemCount = 0,
    currentMenuItemId,
  } = props;

  const navigate = useNavigate();
  const urlParams = useParams();
  const hasTableId = !!urlParams?.table_id;

  const [confirmed, setConfirmed] = useState(false);
  const [recentlyAdded, setRecentlyAdded] = useState(false);
  const confirmTimerRef = useRef(null);
  const recentlyAddedTimerRef = useRef(null);
  const containerRef = useRef(null);

  const currentMenuItemCount = currentMenuItemId
    ? cartItems.find((item) => item?._id === currentMenuItemId)?.count ?? cartItemCount
    : cartItemCount;
  const isCurrentMenuItemInCart = currentMenuItemCount > 0;

  const onAddToCartClick = () => {
    handleAddToCart?.();
    clearTimeout(confirmTimerRef.current);
    setConfirmed(true);
    confirmTimerRef.current = setTimeout(() => setConfirmed(false), 3000);

    clearTimeout(recentlyAddedTimerRef.current);
    setRecentlyAdded(true);
    recentlyAddedTimerRef.current = setTimeout(() => setRecentlyAdded(false), 3000);
  };

  const onInfoClick = () => {
    let url;
    if (urlParams?.slug) {
      url = `/r/menu/${urlParams.slug}/table/${urlParams.table_id}`;
    } else if (urlParams?.id) {
      url = `/restaurant_menu/${urlParams.id}/table/${urlParams.table_id}`;
    }
    if (url) {
      navigate(url, { state: { autoOpenMenuId: currentMenuItemId } });
    }
  };

  useEffect(() => {
    clearTimeout(confirmTimerRef.current);
    clearTimeout(recentlyAddedTimerRef.current);
    setConfirmed(false);
    setRecentlyAdded(false);
  }, [currentMenuItemId]);

  useEffect(() => () => {
    clearTimeout(confirmTimerRef.current);
    clearTimeout(recentlyAddedTimerRef.current);
  }, []);

  // ── Add-to-Cart animation state ───────────────────────────────────────────
  // phases: 'hidden' | 'entering' | 'visible' | 'exiting'
  const [addToCartPhase, setAddToCartPhase] = useState('hidden');
  const [showStars, setShowStars] = useState(false);

  useEffect(() => {
    const timers = [];

    function schedule(fn, delay) {
      const id = setTimeout(fn, delay);
      timers.push(id);
    }

    function runCycle(startDelay) {
      let t = startDelay;

      // 1. Grow from a point → overshoot → settle (with star burst)
      schedule(() => { setAddToCartPhase('entering'); setShowStars(true); }, t);
      t += ENTER_DURATION;

      // 2. Stable on-screen – stars already gone (unmounted after animation)
      schedule(() => { setAddToCartPhase('visible'); setShowStars(false); }, t);
      t += VISIBLE_DURATION;

      // 3. Slight grow → shrink to nothing
      schedule(() => setAddToCartPhase('exiting'), t);
      t += EXIT_DURATION;

      // 4. Fully hidden; wait before next cycle
      schedule(() => setAddToCartPhase('hidden'), t);
      t += HIDDEN_DURATION;

      // 5. Loop
      schedule(() => runCycle(0), t);
    }

    runCycle(HIDDEN_DURATION); // first appearance after 2 s

    return () => timers.forEach(clearTimeout);
  }, []);

  const btnViewProps = {
    enterAframeAr,
    enterWebXrArMode,
    showPrevBtn,
    showNextBtn,
    prevIcon,
    nextIcon,
    locale: props.locale,
    menuIcon: props.menuIcon,
    onPrevBtnClick: props.onPrevBtnClick,
    onNextBtnClick: props.onNextBtnClick,
    onRestMenuNavigate: props.onRestMenuNavigate,
  };

  if (enterAframeAr || enterWebXrArMode) {
    return <BtnView {...btnViewProps} />;
  }

  const isHidden = addToCartPhase === 'hidden';
  const btnAnim =
    addToCartPhase === 'entering' ? 'addToCartPopIn  0.7s cubic-bezier(0.34, 1.56, 0.64, 1) forwards' :
      addToCartPhase === 'exiting' ? 'addToCartPopOut 0.5s ease-in forwards' :
        undefined;

  return (
    <Box
      className="device-width"
      sx={{
        ...styles.btnMainView,
        bottom:
          (showCategoryGrp && categoryGrpPosition !== CATEGORY_GRP_DEFAULT_POS
            ? CATEGORY_LIST_HEIGHT
            : 0) + (menuSectionBg ? 10 : 30),
        placeSelf: "center"
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", placeSelf: "center", width: "100%", gap: "12px" }}>
        {hasTableId && (
          <Box
            ref={containerRef}
            sx={{
              alignSelf: 'flex-start',
              px: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: "space-between",
              gap: '10px',
              width: '100%',
              boxSizing: 'border-box',
              width: '100%',
              overflow: "hidden"
            }}>
            {/* Info button */}
            <Box sx={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
              <Button
                variant='outlined'
                sx={{
                  borderRadius: "10px",
                  height: "45px",
                  width: "45px",
                  minWidth: "45px",
                  boxShadow: "0 4px 4px rgba(0, 0, 0, 0.25)",
                  backgroundColor: "#000000",
                  borderColor: "#000000",
                  color: "#FFF",
                  opacity: 0.75,
                  "&:hover, &:focus, &:active": {
                    opacity: 1,
                    backgroundColor: "#000000",
                    borderColor: "#000000",
                    color: "#FFF",
                  },
                  "& path": { fill: "#FFFFFF" },
                }}
                onClick={onInfoClick}
              >
                <InfoIcon width={20} height={20} />
              </Button>
            </Box>

            {/* Right side: 3 states */}
            {recentlyAdded || confirmed ? (
              /* State 1: just added — temporary confirmation */
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  flexShrink: 0,
                  background: '#000000',
                  padding: '8px 14px',
                  borderRadius: '10px',
                  opacity: 0.85,
                }}
                onClick={onViewCart}
              >
                <TickIcon width={"14px"} height={"14px"} />
                <Typography sx={{ fontSize: 13, color: '#59bb5e', fontFamily: 'BauhausMedium', whiteSpace: 'nowrap' }}>
                  Item Added to Cart
                </Typography>
              </Box>
            ) : isCurrentMenuItemInCart ? (
              /* State 2: item already in cart — show only View Cart */
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  flexShrink: 0,
                  backgroundColor: '#000000',
                  borderRadius: '10px',
                  opacity: 0.85,
                  height: '45px',
                }}
              >
                <Button
                  onClick={onViewCart}
                  sx={{
                    fontSize: 12,
                    textTransform: 'none',
                    fontFamily: 'BauhausMedium',
                    color: '#FFF',
                    px: 2,
                    height: '100%',
                    borderRadius: '10px',
                    whiteSpace: 'nowrap',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
                  }}
                >
                  Item in Cart
                </Button>
              </Box>
            ) : (
              /* Default: animated Add to Cart button */
              <Box sx={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
                {showStars && <StarParticles />}
                <Button
                  variant='outlined'
                  sx={{
                    fontSize: "14px",
                    borderRadius: "10px",
                    height: "45px",
                    width: "120px",
                    boxShadow: "0 4px 4px rgba(0, 0, 0, 0.25)",
                    textTransform: "capitalize",
                    backgroundColor: "#000000",
                    borderColor: "#000000",
                    color: "#FFF",
                    opacity: isHidden ? 0 : 0.75,
                    transform: isHidden ? 'scale(0)' : undefined,
                    animation: btnAnim,
                    pointerEvents: addToCartPhase === 'visible' ? 'auto' : 'none',
                    transition: 'none',
                    "&:hover, &:focus, &:active": {
                      opacity: 1,
                      backgroundColor: "#000000",
                      borderColor: "#000000",
                      color: "#FFF",
                    },
                  }}
                  onClick={onAddToCartClick}
                >
                  Add to Cart
                </Button>
              </Box>
            )}
          </Box>
        )}
        <Box className={"next-prev-btn-device-width"}>
          {menuSectionBg ? (
            <Box sx={styles.bottomView}>
              <img src={menuSectionBg} style={styles.imgStyle} />
              <BtnView {...btnViewProps} />
            </Box>
          ) : (
            <Box sx={styles.btnView}>
              <Box sx={styles.btnView2}></Box>
              <BtnView {...btnViewProps} />
              <Box sx={styles.btnView3}></Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default AppNextPrevBtnTemp2;

const styles = {
  btnMainView: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "fixed",
    left: 0,
    right: 0,
    zIndex: 8,
  },
  bottomView: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    minHeight: 80,
    width: "100%",
  },
  imgStyle: {
    width: "100%",
    height: "100%",
  },
  btnView: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    height: 80,
    width: "100%",
    borderRadius: 6,
    border: "0.03px solid #00000030",
    background: "linear-gradient(#00000020, #94949420)",
  },
  btnView2: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "absolute",
    height: 120,
    width: 120,
    borderRadius: "50%",
    border: "0.03px solid #00000030",
    backdropFilter: "blur(5px)",
    background: "linear-gradient(#00000020, #94949420)",
  },
  btnView3: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "absolute",
    height: "100%",
    width: "100%",
    backdropFilter: "blur(5px)",
    borderRadius: 6,
  },
  prevMainView: {
    display: "flex",
    alignItems: "center",
    position: "absolute",
    zIndex: 8,
    left: 0,
    px: 2,
  },
  nextMainView: {
    display: "flex",
    alignItems: "center",
    position: "absolute",
    zIndex: 7,
    right: 0,
    px: 2,
  },
  menuBtnView: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "absolute",
    width: 100,
    height: 100,
    borderRadius: "50%",
    overflow: "hidden",
    cursor: "pointer",
    zIndex: 7,
    px: 2,
  },
};
